import { useState, useEffect, useCallback, useRef } from 'react';
import { Archive, ArchiveRestore, Bell, Download, FileUp, KeyRound, Mail, Pencil, Plus, Trash2, UserCheck, UserX } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import Tag from '../ui/Tag';
import Tabs from '../ui/Tabs';
import Table from '../ui/Table';
import Modal from '../ui/Modal';
import Input from '../ui/Input';
import Select from '../ui/Select';
import Textarea from '../ui/Textarea';
import Checkbox from '../ui/Checkbox';
import Switch from '../ui/Switch';
import DatePicker from '../ui/DatePicker';
import Skeleton from '../ui/Skeleton';
import { useConfirm } from '../ui/Confirm';
import { useToast } from '../ui/Toast';
import { usePageHeader } from '../shell/pageHeader';
import { clean } from '../shell/copy';
import ClientDownloads from '../components/ClientDownloads';
import PtoTracker from '../components/PtoTracker';
import SceneScrubAdmin from '../components/SceneScrubAdmin';
import ScrubQueueAdmin from '../components/ScrubQueueAdmin';
import OutputTargets from '../components/OutputTargets';

// Admin on src/ui (DESIGN rev 2.1 §3.1: "Admin, Reports, Time off: Table + Tabs"). Same tabs, same role gates,
// same calls with the same parameters as the old page. Create and edit forms are Modals; every confirm is useConfirm.

// ── Constants ─────────────────────────────────────────────────────────────────

const SERVICE_TYPES = [
  'In_House_Live', 'Off_Site_Live', 'Off_Site_Skit',
  'Off_Site_Pod', 'Off_Site_Riff', 'In_House_Riff', 'CTF', 'Cooking',
];
const SCRIPT_FORMATS = ['Skit', 'TH', 'AI', 'Mockumentary', 'None', 'Recipe', 'Longform'];
const PRODUCTION_TRACKS = ['Live', 'AI', 'Both'];
const LOCATION_TYPES = ['SSD', 'Frame.io', 'Cloud', 'Other'];
const AUDIT_ENTITY_TYPES = ['script', 'live_production', 'ai_production', 'client', 'team_member', 'posting_scheduler'];
const INTERNAL_ROLES = ['Writer', 'Qual_Reviewer', 'Editor', 'Producer', 'Post_Lead', 'Admin', 'Manager', 'Power_User'];
const EXTERNAL_ROLES = ['Writer', 'Qual_Reviewer', 'Editor', 'Manager'];
const GENDER_OPTIONS = [
  { value: '', label: 'Not set' },
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'either', label: 'Either (can play any gender role)' },
];

const roleLabel = role => role.replace(/_/g, ' ');
const underscores = s => (s || '').replace(/_/g, ' ');
const formatLabel = f => (f === 'TH' ? 'Talking head' : f);
const errOf = (e, fallback) => clean(e?.response?.data?.error) || fallback;

function fmtDateTime(str) {
  if (!str) return '';
  return new Date(str).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function emptyClient() {
  return {
    name: '', initials: '', serviceType: 'In_House_Live',
    productionTrack: 'Live', contactName: '', contactEmail: '',
    contactPhone: '', postingChannel: '', allowedScriptFormats: [], notes: '',
  };
}
function emptyMember() {
  return { name: '', initials: '', email: '', password: '', roles: [], isExternal: false, assignedClientIds: [], talentGender: '' };
}
function emptyStorage() {
  return { label: '', locationType: 'SSD', pathOrUrl: '', notes: '' };
}

// The page header's create button lives outside the tab; it bumps a counter the tab watches. The tab ignores the
// value it mounted with, so switching tabs never opens a form by itself.
function useCreateSignal(n, open) {
  const first = useRef(n);
  useEffect(() => { if (n !== first.current) open(); }, [n]); // eslint-disable-line react-hooks/exhaustive-deps
}

const none = <span className="ui-text-3">None</span>;

// ── Project modal ─────────────────────────────────────────────────────────────

function ClientModal({ client, onClose, onSaved }) {
  const isEdit = Boolean(client?.id);
  const [form, setForm] = useState(isEdit ? { ...client, allowedScriptFormats: client.allowedScriptFormats || [] } : emptyClient());
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = (field, val) => setForm(f => ({ ...f, [field]: val }));
  const toggleFormat = fmt => setForm(f => ({
    ...f,
    allowedScriptFormats: f.allowedScriptFormats.includes(fmt) ? f.allowedScriptFormats.filter(x => x !== fmt) : [...f.allowedScriptFormats, fmt],
  }));

  async function handleSave(e) {
    e?.preventDefault();
    if (!form.name.trim() || !form.initials.trim() || !form.serviceType || !form.productionTrack) {
      setErr('Name, initials, service type and production track are required.');
      return;
    }
    setSaving(true); setErr('');
    try {
      if (isEdit) await api.patch(`/api/clients/${client.id}`, form);
      else await api.post('/api/clients', form);
      onSaved(isEdit ? 'Project saved.' : 'Project created.');
    } catch (e2) {
      setErr(errOf(e2, 'The project did not save. Try again.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal open onClose={onClose} title={isEdit ? 'Edit project' : 'New project'}
      footer={<>
        <Button variant="quiet" onClick={onClose}>Cancel</Button>
        <Button variant="primary" loading={saving} onClick={handleSave}>{isEdit ? 'Save project' : 'Create project'}</Button>
      </>}>
      <form className="ui-stack" onSubmit={handleSave}>
        <div className="ui-cols ui-cols--tight">
          <Input label="Name" value={form.name} onChange={e => set('name', e.target.value)} placeholder="Project name" data-autofocus />
          <Input label="Initials" value={form.initials} onChange={e => set('initials', e.target.value)} placeholder="e.g. JD" maxLength={6} />
          <Select label="Service type" value={form.serviceType} onChange={e => set('serviceType', e.target.value)}
            options={SERVICE_TYPES.map(t => ({ value: t, label: underscores(t) }))} />
          <Select label="Production track" value={form.productionTrack} onChange={e => set('productionTrack', e.target.value)} options={PRODUCTION_TRACKS} />
          <Input label="Contact name" value={form.contactName || ''} onChange={e => set('contactName', e.target.value)} placeholder="Full name" />
          <Input label="Contact email" type="email" value={form.contactEmail || ''} onChange={e => set('contactEmail', e.target.value)} placeholder="email@example.com" />
          <Input label="Contact phone" value={form.contactPhone || ''} onChange={e => set('contactPhone', e.target.value)} placeholder="Phone number" />
          <Input label="Posting channel" value={form.postingChannel || ''} onChange={e => set('postingChannel', e.target.value)} placeholder="@handle or URL" />
        </div>
        <fieldset className="ui-fieldset ui-stack ui-stack--tight">
          <legend className="ui-field__label">Allowed script formats</legend>
          <div className="ui-cluster">
            {SCRIPT_FORMATS.map(fmt => (
              <Checkbox key={fmt} label={formatLabel(fmt)} checked={form.allowedScriptFormats.includes(fmt)} onChange={() => toggleFormat(fmt)} />
            ))}
          </div>
        </fieldset>
        <Textarea label="Notes" value={form.notes || ''} onChange={e => set('notes', e.target.value)} placeholder="Anything the team should know about this project" />
        {err && <p className="ui-p ui-field__error" role="alert">{err}</p>}
      </form>
    </Modal>
  );
}

// ── Team member modal ─────────────────────────────────────────────────────────

function MemberModal({ member, clients, talentScoped = false, onClose, onSaved }) {
  const isEdit = Boolean(member?.id);
  const [form, setForm] = useState(isEdit
    ? {
        name: member.name,
        initials: member.initials,
        isActive: member.isActive,
        roles: member.roles?.map(r => r.role) || [],
        isExternal: member.isExternal || false,
        assignedClientIds: member.assignedClients?.map(c => c.id) || [],
        talentGender: member.talentGender || '',
      }
    : emptyMember());
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const firstName = form.name.trim().split(' ')[0].toLowerCase();
  const passwordPlaceholder = firstName ? `${firstName}_temp123` : 'firstname_temp123';
  const availableRoles = form.isExternal ? EXTERNAL_ROLES : INTERNAL_ROLES;
  const set = (field, val) => setForm(f => ({ ...f, [field]: val }));
  const toggleRole = role => setForm(f => ({ ...f, roles: f.roles.includes(role) ? f.roles.filter(r => r !== role) : [...f.roles, role] }));
  const toggleClient = id => setForm(f => ({
    ...f,
    assignedClientIds: f.assignedClientIds.includes(id) ? f.assignedClientIds.filter(x => x !== id) : [...f.assignedClientIds, id],
  }));

  function handleExternalToggle(isExternal) {
    setForm(f => ({
      ...f,
      isExternal,
      assignedClientIds: isExternal ? f.assignedClientIds : [],
      // Strip restricted roles when switching to external
      roles: isExternal ? f.roles.filter(r => EXTERNAL_ROLES.includes(r)) : f.roles,
    }));
  }

  async function handleSave(e) {
    e?.preventDefault();
    if (!form.name.trim() || !form.initials.trim() || (!isEdit && !form.email.trim())) {
      setErr('Name, initials and email are required.');
      return;
    }
    if (form.isExternal && form.assignedClientIds.length === 0) {
      setErr('An external member needs at least one assigned project.');
      return;
    }
    setSaving(true); setErr('');
    try {
      if (isEdit) {
        await api.patch(`/api/team-members/${member.id}`, {
          name: form.name,
          initials: form.initials,
          isActive: form.isActive,
          isExternal: form.isExternal,
          assignedClientIds: form.isExternal ? form.assignedClientIds : [],
          talentGender: form.talentGender || null,
        });
        // Role changes are admin-only; talent-scoped editors skip the roles PUT.
        if (!talentScoped) await api.put(`/api/team-members/${member.id}/roles`, { roles: form.roles });
      } else {
        await api.post('/api/team-members', {
          name: form.name,
          initials: form.initials,
          email: form.email,
          password: form.password.trim() || passwordPlaceholder,
          roles: talentScoped ? ['Talent'] : form.roles,
          isExternal: talentScoped ? false : form.isExternal,
          assignedClientIds: (!talentScoped && form.isExternal) ? form.assignedClientIds : [],
          talentGender: form.talentGender || null,
        });
      }
      onSaved(isEdit ? 'Member saved.' : `${form.name.trim()} added.`);
    } catch (e2) {
      setErr(errOf(e2, 'The member did not save. Try again.'));
    } finally {
      setSaving(false);
    }
  }

  const title = isEdit ? 'Edit member' : talentScoped ? 'New talent' : 'New team member';
  return (
    <Modal open onClose={onClose} title={title}
      footer={<>
        <Button variant="quiet" onClick={onClose}>Cancel</Button>
        <Button variant="primary" loading={saving} onClick={handleSave}>{isEdit ? 'Save changes' : 'Create member'}</Button>
      </>}>
      <form className="ui-stack" onSubmit={handleSave}>
        <div className="ui-cols ui-cols--tight">
          <Input label="Name" value={form.name} onChange={e => set('name', e.target.value)} placeholder="Full name" data-autofocus />
          <Input label="Initials" value={form.initials} onChange={e => set('initials', e.target.value)} placeholder="e.g. JD" maxLength={4} />
        </div>

        {!isEdit && <>
          <Input label="Email" type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="name@example.com" />
          <Input label="Password" value={form.password} onChange={e => set('password', e.target.value)} placeholder={passwordPlaceholder}
            autoComplete="new-password" hint="Leave blank to use the default shown in the field." />
        </>}

        {isEdit && (
          <Select label="Status" value={form.isActive ? 'active' : 'inactive'} onChange={e => set('isActive', e.target.value === 'active')}
            options={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]} />
        )}

        {/* Member type: admin only; talent are always internal */}
        {!talentScoped && (
          <div className="ui-stack ui-stack--tight">
            <span className="ui-field__label" id="member-type">Member type</span>
            <div className="ui-seg" role="group" aria-labelledby="member-type">
              <Button variant="quiet" aria-pressed={!form.isExternal} onClick={() => handleExternalToggle(false)}>Internal</Button>
              <Button variant="quiet" aria-pressed={form.isExternal} onClick={() => handleExternalToggle(true)}>External</Button>
            </div>
            {!form.isExternal && <p className="ui-p ui-dense ui-text-3">Internal members can see every project.</p>}
          </div>
        )}

        {/* Project assignment: external only, several allowed */}
        {form.isExternal && (
          <fieldset className="ui-fieldset ui-stack ui-stack--tight">
            <legend className="ui-field__label">Assigned projects</legend>
            <div className="ui-region ui-region--pad ui-scroll ui-stack ui-stack--tight">
              {clients.length === 0 && <p className="ui-p ui-dense ui-text-3">There are no active projects to assign.</p>}
              {clients.map(c => (
                <Checkbox key={c.id} label={clean(c.name)} checked={form.assignedClientIds.includes(c.id)} onChange={() => toggleClient(c.id)} />
              ))}
            </div>
            <p className="ui-p ui-dense ui-text-3">External members only see scripts and productions for their assigned projects.</p>
          </fieldset>
        )}

        {!talentScoped && (
          <fieldset className="ui-fieldset ui-stack ui-stack--tight">
            <legend className="ui-field__label">Roles</legend>
            <div className="ui-cluster">
              {availableRoles.map(role => (
                <Checkbox key={role} label={roleLabel(role)} checked={form.roles.includes(role)} onChange={() => toggleRole(role)} />
              ))}
            </div>
            {form.isExternal && <p className="ui-p ui-dense ui-text-3">Admin, Power User, Producer and Post Lead are not available to external members.</p>}
          </fieldset>
        )}

        {/* Talent. Admin sees a checkbox (grants the Talent role) and the gender; a talent-scoped editor only the gender. */}
        {talentScoped ? (
          <Select label="Gender (for Script list talent matching)" value={form.talentGender} onChange={e => set('talentGender', e.target.value)} options={GENDER_OPTIONS} />
        ) : (
          <fieldset className="ui-fieldset ui-stack ui-stack--tight">
            <legend className="ui-field__label">Talent</legend>
            <Checkbox label="This member is on-camera talent" checked={form.roles.includes('Talent')} onChange={() => toggleRole('Talent')} />
            {form.roles.includes('Talent') && (
              <Select label="Gender (for Script list talent matching)" value={form.talentGender} onChange={e => set('talentGender', e.target.value)} options={GENDER_OPTIONS} />
            )}
          </fieldset>
        )}

        {err && <p className="ui-p ui-field__error" role="alert">{err}</p>}
      </form>
    </Modal>
  );
}

// ── Storage location modal ────────────────────────────────────────────────────

function StorageModal({ storage, onClose, onSaved }) {
  const isEdit = Boolean(storage?.id);
  const [form, setForm] = useState(isEdit ? { ...storage } : emptyStorage());
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = (field, val) => setForm(f => ({ ...f, [field]: val }));

  async function handleSave(e) {
    e?.preventDefault();
    if (!form.label.trim() || !form.locationType) { setErr('Label and location type are required.'); return; }
    setSaving(true); setErr('');
    try {
      if (isEdit) await api.patch(`/api/storage/${storage.id}`, form);
      else await api.post('/api/storage', form);
      onSaved(isEdit ? 'Location saved.' : 'Location added.');
    } catch (e2) {
      setErr(errOf(e2, 'The location did not save. Try again.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal open onClose={onClose} title={isEdit ? 'Edit storage location' : 'New storage location'}
      footer={<>
        <Button variant="quiet" onClick={onClose}>Cancel</Button>
        <Button variant="primary" loading={saving} onClick={handleSave}>Save location</Button>
      </>}>
      <form className="ui-stack" onSubmit={handleSave}>
        <Input label="Label" value={form.label} onChange={e => set('label', e.target.value)} placeholder="e.g. Main SSD" data-autofocus />
        <Select label="Location type" value={form.locationType} onChange={e => set('locationType', e.target.value)} options={LOCATION_TYPES} />
        <Input label="Path or URL" value={form.pathOrUrl || ''} onChange={e => set('pathOrUrl', e.target.value)} placeholder="/Volumes/SSD/Projects or https://" />
        <Textarea label="Notes" value={form.notes || ''} onChange={e => set('notes', e.target.value)} placeholder="Anything about this location" />
        {err && <p className="ui-p ui-field__error" role="alert">{err}</p>}
      </form>
    </Modal>
  );
}

// ── Tab: Projects ─────────────────────────────────────────────────────────────

function ClientsTab({ createN }) {
  const [clients, setClients] = useState([]);
  const [state, setState] = useState('loading');
  const [modal, setModal] = useState(null); // null | { client? }
  const confirm = useConfirm();
  const toast = useToast();
  useCreateSignal(createN, () => setModal({ client: null }));

  const load = useCallback(async () => {
    setState(s => (s === 'data' ? s : 'loading'));
    try {
      const res = await api.get('/api/clients?includeInactive=true');
      setClients(res.data);
      setState('data');
    } catch { setState('error'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function archive(c) {
    const ok = await confirm({ title: `Archive ${clean(c.name)}?`, body: 'The project is hidden from active lists. You can restore it here later.', confirmLabel: 'Archive' });
    if (!ok) return;
    try { await api.delete(`/api/clients/${c.id}`); toast(`${clean(c.name)} archived.`); load(); }
    catch (e) { toast(errOf(e, 'The project did not archive. Try again.'), { tone: 'danger' }); }
  }
  async function restore(c) {
    try { await api.patch(`/api/clients/${c.id}`, { isActive: true }); toast(`${clean(c.name)} restored.`); load(); }
    catch (e) { toast(errOf(e, 'The project did not restore. Try again.'), { tone: 'danger' }); }
  }

  const columns = [
    { key: 'name', label: 'Name', width: '22%', render: c => (c.isActive ? clean(c.name) : <span className="ui-text-3">{clean(c.name)}</span>) },
    { key: 'initials', label: 'Initials', width: 88 },
    { key: 'serviceType', label: 'Service type', render: c => underscores(c.serviceType) },
    { key: 'productionTrack', label: 'Track', width: 88 },
    { key: 'contact', label: 'Contact', render: c => (c.contactName ? <span title={c.contactEmail || ''}>{clean(c.contactName)}</span> : none) },
    { key: 'active', label: 'State', width: 112, render: c => (c.isActive ? <Badge tone="ok">Active</Badge> : <span className="ui-text-3">Archived</span>) },
    { key: 'actions', label: <span className="ui-sr">Actions</span>, width: 96, render: c => (
      <span className="ui-cluster">
        <Button variant="quiet" iconOnly icon={Pencil} label={`Edit ${clean(c.name)}`} onClick={() => setModal({ client: c })} />
        {c.isActive
          ? <Button variant="quiet" iconOnly icon={Archive} label={`Archive ${clean(c.name)}`} onClick={() => archive(c)} />
          : <Button variant="quiet" iconOnly icon={ArchiveRestore} label={`Restore ${clean(c.name)}`} onClick={() => restore(c)} />}
      </span>
    ) },
  ];

  return (
    <section className="ui-section" aria-label="Projects">
      <div className="ui-region">
        <Table caption="Projects" columns={columns} rows={clients} state={state}
          error={{ title: 'Projects did not load', message: 'Check your connection, then try again.', onRetry: load }}
          empty={{ message: 'No projects yet. Create the first one to start assigning scripts.', action: { label: 'New project', icon: Plus, onClick: () => setModal({ client: null }) } }} />
      </div>
      {modal && (
        <ClientModal client={modal.client} onClose={() => setModal(null)}
          onSaved={msg => { setModal(null); toast(msg); load(); }} />
      )}
    </section>
  );
}

// ── Notification settings modal (admin manages another member's preferences) ──

const ROLE_NORM_MAP = { Qual_Reviewer: 'Qual Reviewer', Post_Lead: 'Post Lead', Power_User: 'Power User' };

function UserSettingsModal({ member, onClose }) {
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState('');
  const [notifEnabled, setNotifEnabled] = useState(true);
  const [eventTypes, setEventTypes] = useState([]);
  const [prefs, setPrefs] = useState({});
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const toast = useToast();

  useEffect(() => {
    Promise.all([
      api.get('/api/notifications/event-types'),
      api.get(`/api/notifications/admin/event-preferences/${member.id}`),
      api.get(`/api/notifications/admin/preferences/${member.id}`),
    ]).then(([typesRes, prefsRes, globalRes]) => {
      setEventTypes(typesRes.data);
      const map = {};
      prefsRes.data.forEach(p => { map[p.eventType] = p; });
      setPrefs(map);
      setNotifEnabled(globalRes.data.notificationsEnabled);
    }).catch(() => setLoadErr('The notification settings did not load. Close this and try again.'))
      .finally(() => setLoading(false));
  }, [member.id]);

  function togglePref(eventType, channel) {
    setPrefs(prev => ({ ...prev, [eventType]: { ...prev[eventType], eventType, [channel]: !prev[eventType]?.[channel] } }));
    setErr('');
  }

  async function handleSave() {
    setSaving(true); setErr('');
    try {
      const payload = Object.values(prefs).map(p => ({
        eventType: p.eventType,
        emailEnabled: p.emailEnabled ?? true,
        inAppEnabled: p.inAppEnabled ?? true,
      }));
      await Promise.all([
        api.patch(`/api/notifications/admin/preferences/${member.id}`, { notificationsEnabled: notifEnabled }),
        ...(payload.length > 0 ? [api.put(`/api/notifications/admin/event-preferences/${member.id}`, payload)] : []),
      ]);
      toast('Settings saved.');
    } catch {
      setErr('The settings did not save. Try again.');
    } finally {
      setSaving(false);
    }
  }

  const memberRoles = (member.roles || []).map(r => ROLE_NORM_MAP[r.role] || r.role);

  return (
    <Modal open onClose={onClose} title={`Notifications for ${clean(member.name)}`}
      footer={<>
        <Button variant="quiet" onClick={onClose}>Close</Button>
        <Button variant="primary" loading={saving} disabled={loading || !!loadErr} onClick={handleSave}>Save settings</Button>
      </>}>
      {loading ? (
        <div className="ui-stack" role="status" aria-label="Loading notification settings"><Skeleton width="50%" /><Skeleton width="80%" /><Skeleton width="70%" /></div>
      ) : loadErr ? (
        <p className="ui-p ui-field__error" role="alert">{loadErr}</p>
      ) : (
        <div className="ui-stack">
          <Switch label={notifEnabled ? 'All notifications on' : 'All notifications off'} checked={notifEnabled}
            onChange={() => { setNotifEnabled(v => !v); setErr(''); }} />
          <div className="ui-list" aria-label="Per-event preferences">
            {eventTypes.map(t => {
              const pref = prefs[t.type] || {};
              const relevant = t.roles.some(r => memberRoles.includes(r));
              return (
                <div key={t.type} className="ui-pair ui-pair--auto">
                  <div className="ui-stack ui-stack--tight">
                    <span className={relevant ? 'ui-dense' : 'ui-dense ui-text-3'}>{clean(t.label)}</span>
                    <span className="ui-dense ui-text-3">{clean(t.description)}</span>
                    {!relevant && <span className="ui-dense ui-text-3">Not used by this member's roles.</span>}
                  </div>
                  <div className="ui-cluster">
                    <Switch label="In-app" checked={pref.inAppEnabled ?? true} onChange={() => togglePref(t.type, 'inAppEnabled')} />
                    <Switch label="Email" checked={pref.emailEnabled ?? true} onChange={() => togglePref(t.type, 'emailEnabled')} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {err && <p className="ui-p ui-field__error" role="alert">{err}</p>}
    </Modal>
  );
}

// ── Tab: Team members ─────────────────────────────────────────────────────────

function TeamTab({ talentScoped = false, createN }) {
  const [members, setMembers] = useState([]);
  const [clients, setClients] = useState([]);
  const [state, setState] = useState('loading');
  const [modal, setModal] = useState(null); // null | { type: 'form', member? } | { type: 'settings', member }
  const confirm = useConfirm();
  const toast = useToast();
  useCreateSignal(createN, () => setModal({ type: 'form', member: null }));

  const load = useCallback(async () => {
    setState(s => (s === 'data' ? s : 'loading'));
    try {
      const [membersRes, clientsRes] = await Promise.all([
        api.get('/api/team-members?includeInactive=true'),
        api.get('/api/clients'),
      ]);
      setMembers(membersRes.data);
      setClients(clientsRes.data);
      setState('data');
    } catch { setState('error'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, okMsg, failMsg) => {
    try { const res = await fn(); toast(typeof okMsg === 'function' ? okMsg(res) : okMsg); load(); }
    catch (e) { toast(errOf(e, failMsg), { tone: 'danger' }); }
  };

  async function deactivate(m) {
    const ok = await confirm({ title: `Deactivate ${clean(m.name)}?`, body: 'They will no longer be able to log in. You can reactivate them here.', confirmLabel: 'Deactivate' });
    if (ok) run(() => api.patch(`/api/team-members/${m.id}`, { isActive: false }), `${clean(m.name)} deactivated.`, 'The member did not deactivate. Try again.');
  }
  const activate = m => run(() => api.patch(`/api/team-members/${m.id}`, { isActive: true }), `${clean(m.name)} reactivated.`, 'The member did not reactivate. Try again.');
  async function forcePassword(m) {
    const ok = await confirm({ title: 'Force a password change?', body: `${clean(m.name)} will have to set a new password on their next login.`, confirmLabel: 'Force change' });
    if (ok) run(() => api.patch(`/api/team-members/${m.id}`, { mustChangePassword: true }), `${clean(m.name)} will set a new password at next login.`, 'The change did not save. Try again.');
  }
  async function sendReset(m) {
    const ok = await confirm({ title: 'Send a password reset email?', body: `${clean(m.name)} (${m.email}) gets a link to set a new password.`, confirmLabel: 'Send email' });
    if (!ok) return;
    try {
      const res = await api.post(`/api/team-members/${m.id}/send-reset`);
      toast(clean(res.data?.message) || `Password reset email sent to ${clean(m.name)}.`);
    } catch (e) {
      toast(errOf(e, 'The reset email did not send. Try again.'), { tone: 'danger' });
    }
  }
  async function hardDelete(m) {
    const ok = await confirm({
      title: `Delete ${clean(m.name)}?`,
      body: 'They are removed from the team, lose all access at once and stop receiving notifications and emails. Their scripts, productions and audit history are kept. This cannot be undone here.',
      confirmLabel: 'Delete member', destructive: true,
    });
    if (ok) run(() => api.delete(`/api/team-members/${m.id}`), `${clean(m.name)} deleted.`, 'Delete failed.');
  }

  // In talent-scoped mode (Power User / Producer) only talent are listed.
  const displayMembers = talentScoped ? members.filter(m => (m.roles || []).some(r => r.role === 'Talent')) : members;

  const columns = [
    { key: 'name', label: 'Name', width: '18%', render: m => (m.isActive ? clean(m.name) : <span className="ui-text-3">{clean(m.name)}</span>) },
    { key: 'initials', label: 'Initials', width: 80 },
    { key: 'email', label: 'Email', width: '20%', render: m => <span title={m.email}>{m.email}</span> },
    { key: 'roles', label: 'Roles', render: m => {
      const projects = m.assignedClients?.map(c => clean(c.name)).join(', ');
      return (
        <span className="ui-cluster">
          {m.isExternal && <Tag title={projects ? `External: ${projects}` : 'External'}>{projects ? `External: ${projects}` : 'External'}</Tag>}
          {m.roles && m.roles.length > 0 ? m.roles.map(r => <Tag key={r.role}>{roleLabel(r.role)}</Tag>) : <span className="ui-text-3">No roles</span>}
        </span>
      );
    } },
    { key: 'active', label: 'State', width: 104, render: m => (m.isActive ? <Badge tone="ok">Active</Badge> : <span className="ui-text-3">Inactive</span>) },
    { key: 'actions', label: <span className="ui-sr">Actions</span>, width: talentScoped ? 224 : 264, render: m => (
      <span className="ui-cluster">
        <Button variant="quiet" iconOnly icon={Pencil} label={`Edit ${clean(m.name)}`} onClick={() => setModal({ type: 'form', member: m })} />
        <Button variant="quiet" iconOnly icon={Bell} label={`Notification settings for ${clean(m.name)}`} onClick={() => setModal({ type: 'settings', member: m })} />
        <Button variant="quiet" iconOnly icon={KeyRound} label={`Force ${clean(m.name)} to change password at next login`} onClick={() => forcePassword(m)} />
        <Button variant="quiet" iconOnly icon={Mail} label={`Send ${clean(m.name)} a password reset email`} onClick={() => sendReset(m)} />
        {m.isActive
          ? <Button variant="quiet" iconOnly icon={UserX} label={`Deactivate ${clean(m.name)}`} onClick={() => deactivate(m)} />
          : <Button variant="quiet" iconOnly icon={UserCheck} label={`Reactivate ${clean(m.name)}`} onClick={() => activate(m)} />}
        {/* Hard delete: admin only; hidden in talent-scoped mode */}
        {!talentScoped && <Button variant="quiet" iconOnly icon={Trash2} label={`Delete ${clean(m.name)}`} onClick={() => hardDelete(m)} />}
      </span>
    ) },
  ];

  return (
    <section className="ui-section" aria-label={talentScoped ? 'Talent' : 'Team members'}>
      <div className="ui-region">
        <Table caption={talentScoped ? 'Talent' : 'Team members'} columns={columns} rows={displayMembers} state={state}
          error={{ title: 'Team members did not load', message: 'Check your connection, then try again.', onRetry: load }}
          empty={{
            message: talentScoped ? 'No talent yet. Add the first one so scripts can be cast.' : 'No team members yet. Add the first one to give them a login.',
            action: { label: talentScoped ? 'New talent' : 'New member', icon: Plus, onClick: () => setModal({ type: 'form', member: null }) },
          }} />
      </div>
      {modal?.type === 'form' && (
        <MemberModal member={modal.member} clients={clients} talentScoped={talentScoped} onClose={() => setModal(null)}
          onSaved={msg => { setModal(null); toast(msg); load(); }} />
      )}
      {modal?.type === 'settings' && <UserSettingsModal member={modal.member} onClose={() => setModal(null)} />}
    </section>
  );
}

// ── Tab: Storage locations ────────────────────────────────────────────────────

function StorageTab({ createN }) {
  const [locations, setLocations] = useState([]);
  const [state, setState] = useState('loading');
  const [modal, setModal] = useState(null); // null | { storage? }
  const confirm = useConfirm();
  const toast = useToast();
  useCreateSignal(createN, () => setModal({ storage: null }));

  const load = useCallback(async () => {
    setState(s => (s === 'data' ? s : 'loading'));
    try {
      const res = await api.get('/api/storage');
      setLocations(res.data);
      setState('data');
    } catch { setState('error'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function remove(loc) {
    const ok = await confirm({ title: `Delete ${clean(loc.label)}?`, body: 'The storage location is removed. This cannot be undone.', confirmLabel: 'Delete location', destructive: true });
    if (!ok) return;
    try { await api.delete(`/api/storage/${loc.id}`); toast(`${clean(loc.label)} deleted.`); load(); }
    catch (e) { toast(errOf(e, 'The location did not delete. Try again.'), { tone: 'danger' }); }
  }

  const columns = [
    { key: 'label', label: 'Label', width: '22%', render: l => clean(l.label) },
    { key: 'locationType', label: 'Type', width: 104 },
    { key: 'pathOrUrl', label: 'Path or URL', width: '30%', render: l => (l.pathOrUrl ? <span title={l.pathOrUrl}>{l.pathOrUrl}</span> : none) },
    { key: 'notes', label: 'Notes', render: l => (l.notes ? <span title={clean(l.notes)}>{clean(l.notes)}</span> : none) },
    { key: 'actions', label: <span className="ui-sr">Actions</span>, width: 96, render: l => (
      <span className="ui-cluster">
        <Button variant="quiet" iconOnly icon={Pencil} label={`Edit ${clean(l.label)}`} onClick={() => setModal({ storage: l })} />
        <Button variant="quiet" iconOnly icon={Trash2} label={`Delete ${clean(l.label)}`} onClick={() => remove(l)} />
      </span>
    ) },
  ];

  return (
    <section className="ui-section" aria-label="Storage locations">
      <div className="ui-region">
        <Table caption="Storage locations" columns={columns} rows={locations} state={state}
          error={{ title: 'Storage locations did not load', message: 'Check your connection, then try again.', onRetry: load }}
          empty={{ message: 'No storage locations yet. Add the drives and links where footage lives.', action: { label: 'New location', icon: Plus, onClick: () => setModal({ storage: null }) } }} />
      </div>
      {modal && (
        <StorageModal storage={modal.storage} onClose={() => setModal(null)}
          onSaved={msg => { setModal(null); toast(msg); load(); }} />
      )}
    </section>
  );
}

// ── Tab: Audit log ────────────────────────────────────────────────────────────

function AuditTab() {
  const [logs, setLogs] = useState([]);
  const [state, setState] = useState('loading');
  const [filters, setFilters] = useState({ entityType: '', from: '', to: '' });
  const setFilter = (field, val) => setFilters(f => ({ ...f, [field]: val }));

  const fetchLogs = useCallback(async (f) => {
    setState('loading');
    try {
      const params = new URLSearchParams({ limit: 100 });
      if (f.entityType) params.set('entityType', f.entityType);
      if (f.from) params.set('from', f.from);
      if (f.to) params.set('to', f.to);
      const res = await api.get(`/api/audit?${params.toString()}`);
      setLogs(res.data);
      setState('data');
    } catch { setState('error'); }
  }, []);

  // Initial load on mount, unfiltered; the filters apply when Load is pressed.
  useEffect(() => { fetchLogs({}); }, [fetchLogs]);
  const load = () => fetchLogs(filters);

  const value = v => (v != null ? <span title={clean(String(v))}>{clean(String(v))}</span> : none);
  const columns = [
    { key: 'changedAt', label: 'Date and time', width: 184, render: e => <span className="ui-num">{fmtDateTime(e.changedAt)}</span> },
    { key: 'entityType', label: 'Entity type', width: 136, render: e => underscores(e.entityType) },
    { key: 'fieldChanged', label: 'Field', width: 144, render: e => e.fieldChanged || none },
    { key: 'oldValue', label: 'Old value', render: e => value(e.oldValue) },
    { key: 'newValue', label: 'New value', render: e => value(e.newValue) },
    { key: 'changedBy', label: 'Changed by', width: 152, render: e => (e.changedByMember?.name ? clean(e.changedByMember.name) : <span className="ui-text-3">System</span>) },
  ];

  return (
    <section className="ui-section" aria-label="Audit log">
      <form className="ui-toolbar" onSubmit={e => { e.preventDefault(); load(); }}>
        <Select aria-label="Entity type" className="ui-fit" value={filters.entityType} onChange={e => setFilter('entityType', e.target.value)}
          options={[{ value: '', label: 'All entity types' }, ...AUDIT_ENTITY_TYPES.map(t => ({ value: t, label: underscores(t) }))]} />
        <DatePicker aria-label="From date" value={filters.from} onChange={e => setFilter('from', e.target.value)} />
        <span className="ui-dense ui-text-3">to</span>
        <DatePicker aria-label="To date" value={filters.to} onChange={e => setFilter('to', e.target.value)} />
        <Button type="submit" variant="secondary" loading={state === 'loading'}>Load</Button>
      </form>
      <div className="ui-region">
        <Table caption="Audit log" columns={columns} rows={logs} state={state}
          error={{ title: 'The audit log did not load', message: 'Check your connection, then press Load again.', onRetry: load }}
          empty={{ message: 'No changes match these filters. Widen the dates or pick all entity types, then press Load.' }} />
      </div>
    </section>
  );
}

// ── Tab: Import and export ────────────────────────────────────────────────────

// Parses CSV text (handles quoted fields) into an array of row objects keyed by header
function parseCsvText(text) {
  const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n').filter(l => l.trim());
  if (lines.length < 2) return { headers: [], rows: [] };

  function parseRow(line) {
    const vals = [];
    let cur = '', inQ = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') { if (inQ && line[i + 1] === '"') { cur += '"'; i++; } else inQ = !inQ; }
      else if (ch === ',' && !inQ) { vals.push(cur.trim()); cur = ''; }
      else cur += ch;
    }
    vals.push(cur.trim());
    return vals;
  }

  const headers = parseRow(lines[0]);
  const rows = lines.slice(1).map(line => {
    const vals = parseRow(line);
    const obj = {};
    headers.forEach((h, i) => { obj[h] = vals[i] || ''; });
    return obj;
  });
  return { headers, rows };
}

async function triggerDownload(path, filename) {
  const res = await api.get(path, { responseType: 'blob' });
  const url = URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}

function ImportSection({ label, exportPath, templatePath, templateFile, importPath, exportFile, templates }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);  // { headers, rows }
  const [result, setResult] = useState(null);    // { created, errors }
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const fileRef = useRef(null);
  const toast = useToast();

  const download = (path, name) => triggerDownload(path, name).catch(() => toast(`${name} did not download. Try again.`, { tone: 'danger' }));

  function handleFileChange(e) {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f); setResult(null); setErr('');
    const reader = new FileReader();
    reader.onload = ev => setPreview(parseCsvText(ev.target.result));
    reader.readAsText(f);
  }

  async function handleImport() {
    if (!preview?.rows?.length) return;
    setLoading(true); setErr(''); setResult(null);
    try {
      const res = await api.post(importPath, { rows: preview.rows, headers: preview.headers });
      setResult(res.data);
      setFile(null); setPreview(null);
      if (fileRef.current) fileRef.current.value = '';
    } catch (e) {
      setErr(errOf(e, 'Import failed. Check the file against the template, then try again.'));
    } finally { setLoading(false); }
  }

  const headingId = `import-${label.replace(/\W+/g, '-').toLowerCase()}`;
  const previewRows = preview ? preview.rows.slice(0, 5).map((r, i) => ({ ...r, __row: i })) : [];
  const previewCols = preview ? preview.headers.map(h => ({ key: h, label: h, render: r => <span title={r[h]}>{r[h]}</span> })) : [];

  return (
    <section className="ui-section" aria-labelledby={headingId}>
      <h2 className="ui-section__title" id={headingId}>{label}</h2>
      <div className="ui-region ui-region--pad ui-stack">
        <div className="ui-toolbar">
          <Button variant="secondary" icon={Download} onClick={() => download(exportPath, exportFile)}>Export CSV</Button>
          {Array.isArray(templates)
            ? templates.map(t => <Button key={t.label} variant="quiet" icon={Download} onClick={() => download(t.path, t.file)}>Template: {t.label}</Button>)
            : <Button variant="quiet" icon={Download} onClick={() => download(templatePath, templateFile)}>Download template</Button>}
        </div>
        <div className="ui-toolbar">
          <input ref={fileRef} type="file" accept=".csv,text/csv" className="ui-sr" tabIndex={-1} aria-hidden="true" onChange={handleFileChange} />
          <Button variant="secondary" icon={FileUp} onClick={() => fileRef.current?.click()}>{file ? 'Choose another file' : 'Choose CSV file'}</Button>
          {file && <span className="ui-dense ui-text-2">{file.name}</span>}
          {preview && (
            <Button variant="primary" loading={loading} onClick={handleImport}>
              {`Import ${preview.rows.length} row${preview.rows.length !== 1 ? 's' : ''}`}
            </Button>
          )}
        </div>
        {err && <p className="ui-p ui-field__error" role="alert">{err}</p>}
        {result && (
          <div className="ui-stack ui-stack--tight" role="status">
            <span><Badge tone="ok">{`${result.created} record${result.created !== 1 ? 's' : ''} created`}</Badge></span>
            {result.errors?.length > 0 && (
              <div className="ui-stack ui-stack--tight ui-dense">
                {result.errors.map((e, i) => <p key={i} className="ui-p ui-field__error">Row {e.row}: {clean(e.message)}</p>)}
              </div>
            )}
          </div>
        )}
        {preview && preview.rows.length > 0 && (
          <div className="ui-stack ui-stack--tight">
            <p className="ui-p ui-dense ui-text-2">Preview: the first {Math.min(5, preview.rows.length)} of {preview.rows.length} rows</p>
            <Table caption={`${label} import preview`} columns={previewCols} rows={previewRows} rowKey={r => r.__row} />
          </div>
        )}
      </div>
    </section>
  );
}

function DataTab() {
  return (
    <div className="ui-stack ui-stack--loose">
      <p className="ui-section__note">
        Export existing data as CSV, or import records from a CSV file. Download a template to see the exact columns each
        import expects. Project names must match exactly (case does not matter). Writer and team member names must match
        as entered in the system.
      </p>
      <ImportSection
        label="Scripts"
        exportPath="/api/data/scripts/export"
        importPath="/api/data/scripts/import"
        exportFile="scripts-export.csv"
        templates={[
          { label: 'Skit', path: '/api/data/scripts/template/skit', file: 'scripts-skit-template.csv' },
          { label: 'Talking head', path: '/api/data/scripts/template/th', file: 'scripts-th-template.csv' },
          { label: 'AI', path: '/api/data/scripts/template/ai', file: 'scripts-ai-template.csv' },
          { label: 'Mockumentary', path: '/api/data/scripts/template/mockumentary', file: 'scripts-mockumentary-template.csv' },
          { label: 'No format', path: '/api/data/scripts/template/none', file: 'scripts-template.csv' },
        ]}
      />
      <ImportSection
        label="Live production"
        exportPath="/api/data/live-production/export"
        templatePath="/api/data/live-production/template"
        importPath="/api/data/live-production/import"
        exportFile="live-production-export.csv"
        templateFile="live-production-import-template.csv"
      />
      <ImportSection
        label="AI production"
        exportPath="/api/data/ai-production/export"
        templatePath="/api/data/ai-production/template"
        importPath="/api/data/ai-production/import"
        exportFile="ai-production-export.csv"
        templateFile="ai-production-import-template.csv"
      />
    </div>
  );
}

// ── Main Admin page ───────────────────────────────────────────────────────────

const TABS = [
  { id: 'clients', label: 'Projects' },
  { id: 'team', label: 'Team members' },
  { id: 'targets', label: 'Targets' },   // OUT-2: Output daily targets; Admin, Manager, Power User (the API's target writers)
  { id: 'storage', label: 'Storage' },
  { id: 'audit', label: 'Audit log' },
  { id: 'data', label: 'Import and export' },
  { id: 'downloads', label: 'Project downloads' },
  { id: 'pto', label: 'PTO tracker' },
  { id: 'scenescrub', label: 'Scene scrub' },
  { id: 'scrubqueue', label: 'Scrub queue' },   // Q-2 (D-179): Admin role only; the API refuses everyone else
];

export default function Admin() {
  const { hasRole } = useAuth();
  const isAdmin = hasRole('Admin');
  const isPowerUserOnly = hasRole('Power User') && !hasRole('Admin');
  // Admins manage all members; Power Users / Producers get a talent-scoped
  // Team members tab (manage talent only). Other staff don't see the tab.
  const canManageTalent = isAdmin || hasRole('Power User') || hasRole('Producer');
  // Project downloads (bulk client handoff export) is Admin / Power User only.
  const canDownloadClients = isAdmin || hasRole('Power User');
  // PTO tracker (HR module) is Admin / Manager only, NOT Power User.
  const canManagePto = isAdmin || hasRole('Manager');
  const canSetTargets = isAdmin || hasRole('Manager') || hasRole('Power User');
  const visibleTabs = TABS.filter(t => {
    if (t.id === 'clients' && isPowerUserOnly) return false;
    if (t.id === 'team' && !canManageTalent) return false;
    if (t.id === 'downloads' && !canDownloadClients) return false;
    if (t.id === 'pto' && !canManagePto) return false;
    if (t.id === 'targets' && !canSetTargets) return false;
    if (t.id === 'scenescrub' && !canDownloadClients) return false;   // Admin / Power User
    if (t.id === 'scrubqueue' && !isAdmin) return false;              // Q-2: the meter, the queue control and the budget are the Admin's
    return true;
  });
  const defaultTab = isAdmin
    ? 'clients'
    : (canManageTalent ? 'team' : (canManagePto ? 'pto' : (isPowerUserOnly ? 'storage' : 'clients')));
  const [tab, setTab] = useState(defaultTab);
  const [createN, setCreateN] = useState(0);
  const create = () => setCreateN(n => n + 1);

  const ACTIONS = {
    clients: !isPowerUserOnly && <Button variant="primary" icon={Plus} onClick={create}>New project</Button>,
    team: canManageTalent && <Button variant="primary" icon={Plus} onClick={create}>{isAdmin ? 'New member' : 'New talent'}</Button>,
    storage: <Button variant="primary" icon={Plus} onClick={create}>New location</Button>,
  };
  usePageHeader({ title: 'Admin', actions: ACTIONS[tab] || undefined });

  return (
    <div className="ui ui-page">
      <Tabs label="Admin sections" value={tab} onChange={setTab} items={visibleTabs} scroll />
      {tab === 'clients' && !isPowerUserOnly && <ClientsTab createN={createN} />}
      {tab === 'team' && canManageTalent && <TeamTab talentScoped={!isAdmin} createN={createN} />}
      {tab === 'storage' && <StorageTab createN={createN} />}
      {tab === 'audit' && <AuditTab />}
      {tab === 'data' && <DataTab />}
      {tab === 'downloads' && canDownloadClients && <ClientDownloads />}
      {tab === 'pto' && canManagePto && <PtoTracker />}
      {tab === 'targets' && canSetTargets && <OutputTargets />}
      {tab === 'scenescrub' && canDownloadClients && <SceneScrubAdmin />}
      {tab === 'scrubqueue' && isAdmin && <ScrubQueueAdmin />}
    </div>
  );
}
